"use client";

import { useEffect, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";

import { getExistingSessionDestination } from "@/lib/auth/password-login";
import LoginClientPage from "./LoginPage";

type LoginSessionGuardProps = {
  redirectTo: string;
  allowedOrigins: string[];
};

export default function LoginSessionGuard({
  redirectTo,
  allowedOrigins,
}: LoginSessionGuardProps) {
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const sessionCheck = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    let isCurrent = true;

    // Reaproveitar a mesma promise evita duas verificacoes no Strict Mode sem
    // deixar o segundo setup preso ao cleanup do primeiro.
    sessionCheck.current ??= getExistingSessionDestination(
      redirectTo,
      allowedOrigins,
    );

    sessionCheck.current
      .then((destination) => {
        if (!isCurrent) return;

        if (destination) {
          // Navegacao completa para que a rota protegida seja renderizada no
          // servidor com os cookies que acabaram de ser validados.
          window.location.replace(destination);
          return;
        }

        setIsCheckingSession(false);
      })
      .catch(() => {
        // Sessao ausente, expirada ou inconsistente: o usuario continua no
        // fluxo explicito e pode se autenticar normalmente.
        if (isCurrent) setIsCheckingSession(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [allowedOrigins, redirectTo]);

  if (isCheckingSession) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
        <p
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          Verificando sua sessão...
        </p>
      </main>
    );
  }

  return (
    <LoginClientPage redirectTo={redirectTo} allowedOrigins={allowedOrigins} />
  );
}
